import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

export type Dayjs = dayjs.Dayjs;

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(timezone);

const DEFAULT_TIMEZONE = 'America/Chicago';

const formatDateInTimezone = (date: string, tz?: string) => {
	return dayjs(date)
		.tz(tz || DEFAULT_TIMEZONE)
		.format('M/D/YYYY');
};

const formatUTCDateInTimezone = (date: string, tz?: string) => {
	return dayjs(date, 'x')
		.tz(tz || DEFAULT_TIMEZONE)
		.format('M/D/YYYY');
};

const formatDate = (date: string) => {
	return dayjs(date).tz('UTC').format('M/D/YYYY');
};

const today = (format: string) => {
	return dayjs().format(format);
};

const dateXDaysAgo = (x: number, format: string) => {
	return dayjs().subtract(x, 'day').format(format);
};

const todayInTimezone = (format: string, facilityTimezone: string | undefined = 'UTC') => {
	const now = dayjs().tz(facilityTimezone).format(format);
	return dayjs(now).toDate();
};

function formatDateForDisplay(date: string | number | Date | Dayjs, format = 'M/D/YYYY') {
	if (typeof date === 'string') {
		return dayjs(date, 'YYYY-MM-DD').format(format);
	}

	return dayjs(date).format(format);
}

function convertUnixMsToDateInTimezone(
	timestamp: string,
	facilityTimezone: string = DEFAULT_TIMEZONE,
	format: string = 'M/D/YYYY',
) {
	const convertedTime = dayjs(timestamp, 'x').tz(facilityTimezone);
	return formatDateForDisplay(convertedTime, format);
}

const formatDateParkerStatus = (date: string) => (date ? dayjs(date).format('YYYY-MM-DD').split('T')[0] : '');

const formatDateForAccessDevices = (date) => (date ? dayjs(date).format('MM/DD/YYYY') : '');

const getDayjsFromDeviceDate = (date: Dayjs | string | null) => {
	if (!date) return null;
	return typeof date === 'string' ? dayjs.utc(date, 'MM/DD/YYYY') : date;
};

const formatDateForDatePicker = (date) => {
	if (date) {
		if (typeof date === 'string') {
			return date.split('T')[0];
		}

		if (date instanceof Date) {
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, '0');
			const day = String(date.getDate()).padStart(2, '0');

			return `${year}-${month}-${day}`;
		}
	}

	return '';
};

const formatDateParkerStatusForDisplay = (date) => (date ? dayjs(date).utc().format('MM/DD/YYYY') : '');

const convertToDate = (date) => (date ? dayjs(date).toDate() : null);

const dayOfTheMonthFieldOptions = {
	1: '1st',
	2: '2nd',
	3: '3rd',
	4: '4th',
	5: '5th',
	6: '6th',
	7: '7th',
	8: '8th',
	9: '9th',
	10: '10th',
	11: '11th',
	12: '12th',
	13: '13th',
	14: '14th',
	15: '15th',
	16: '16th',
	17: '17th',
	18: '18th',
	19: '19th',
	20: '20th',
	21: '21st',
	22: '22nd',
	23: '23rd',
	24: '24th',
	25: '25th',
	26: '26th',
	27: '27th',
	28: '28th',
};

export {
	convertToDate,
	convertUnixMsToDateInTimezone,
	dateXDaysAgo,
	dayjs,
	dayOfTheMonthFieldOptions,
	formatDate,
	formatDateForAccessDevices,
	formatDateForDatePicker,
	formatDateForDisplay,
	formatDateInTimezone,
	formatDateParkerStatus,
	formatDateParkerStatusForDisplay,
	formatUTCDateInTimezone,
	getDayjsFromDeviceDate,
	today,
	todayInTimezone,
};
